/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Input from '@material-ui/core/Input';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import Tooltip from '@material-ui/core/Tooltip';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { VisibilityOff, Visibility } from '@material-ui/icons';
import {
    object, bool, string, func,
} from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import Info from '@material-ui/icons/Info';
import Cancel from '@material-ui/icons/Cancel';
import { getLanguageName } from '../../../../../../../../../state/ducks/Member/ducks/Auth/helper/helper';

const styles = (theme) => {
    const { palette } = theme;
    return ({
        withBorderColorRootFormControl: {
            border: `solid 1px ${palette.grey8}`,
            width: '100%',
            '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
                '-webkit-box-shadow': '0 0 0 30px white inset !important',
            },
        },
        withoutBorderColorRootFormControl: {
            border: 'solid 1px',
            width: '100%',
            '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
                '-webkit-box-shadow': '0 0 0 30px white inset !important',
            },
        },
        rootErrorIcon: {
            color: palette.error.light,
        },
        rootInputLabel: {
            fontSize: '14px',
            fontWeight: '500',
            textTransform: 'uppercase',
            paddingLeft: '10px',
            paddingTop: '0',
            lineHeight: '14px',
            top: '0',
            '@media screen and (max-width: 599px)': {
                fontSize: '13px',
                fontWeight: '500',
                textTransform: 'uppercase',
                paddingLeft: '10px',
            },
            '@media screen and (max-width: 425px)': {
                fontSize: '11px',
                lineHeight: '10px',
            },
        },
        rootInputBase: {
            marginTop: '24px',
        },
        rootInput: {
            padding: '5px 10px',
            fontWeight: '300',
        },
        asteriskInputLabel: {
            margin: '0px 0px 0px 0px',
            fontSize: '20px',
        },
        rootFormHelperText: {
            margin: '0px',
            padding: '3px 10px',
        },
        formHelperText: {
            fontSize: '12px',
            color: palette.white,
        },
        iconInfoHelpText: {
            backgroundColor: palette.colorNeutral70,
            color: palette.white,
            borderRadius: '50%',
            margin: '3px',
            padding: '0',
            '&:hover, &:focus': {
                backgroundColor: palette.colorNeutral70,
                color: palette.white,
            },
        },
        errorColor: {
            backgroundColor: palette?.error?.main,
        },
        infoColor: {
            backgroundColor: palette.grey10,
        },
        infoArrow: {
            margin: '0px 0px',
            width: '0',
            height: '0',
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderBottom: `5px solid ${palette.grey10}`,
            position: 'absolute',
            right: '18px',
            top: '47px',
        },
        noBorderTop: {
            borderTop: 'none !important',
        },
        '@keyframes stroke': {
            '100%': {
                strokeDashoffset: '0',
            },
        },
        '@keyframes scale': {
            '0%, 100%': {
                transform: 'none',
            },
            '50%': {
                transform: 'scale3d(1.1, 1.1, 1)',
            },
        },
        '@keyframes fill': {
            '100%': {
                boxShadow: `inset 0px 0px 0px 40px ${theme.palette.successButton}`,
            },
        },
        checkmarkCircle: {
            strokeDasharray: '166',
            strokeDashoffset: '166',
            strokeWidth: '2',
            strokeMiterlimit: '10',
            stroke: theme.palette.successButton,
            fill: 'none',
            animation: '$stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards',
        },
        checkmark: {
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            display: 'inline-block',
            strokeWidth: 4,
            stroke: theme.palette.white,
            strokeMiterlimit: '10',
            margin: '-2px 5px 0',
            backgroundColor: theme.palette.colorPrimary,
            boxShadow: `inset 0px 0px 0px ${theme.palette.colorPrimary} !important`,
            animation: '$fill .4s ease-in-out .4s forwards, $scale .3s ease-in-out .9s both',
        },
        checkmarkCheck: {
            transformOrigin: '50% 50%',
            strokeDasharray: '38',
            strokeDashoffset: '5',
            animation: '$stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards',
            backGroundColor: theme.palette.colorPrimary,
        },
        iconInfoContainer: {
            height: 'auto',
            width: '33px',
            display: 'inline-table',
        },
        [theme.breakpoints.down(600)]: {
            phoneLabel: {
                fontSize: 11,
            },
        },
        [theme.breakpoints.down(400)]: {
            phoneLabel: {
                fontSize: 9,
            },
        },
        successIcon: {
            color: 'green',
        },
    });
};

const CustomInputField = (props) => {
    const {
        fieldId,
        name,
        label,
        inputFieldType,
        classes,
        placeholder,
        showInfo,
        isRequired,
        autoComplete,
        handleChange,
        handleBlur,
        handleOnFocus,
        handleOnKeyUp,
        handleOnKeyPress,
        val,
        flagError,
        errorMessage,
        infoHelpText,
        InputProps,
        disabled,
        toggleDisplayFieldInfo,
        showIntputBox,
        startAdornment,
        isPlants,
        isBorderColor,
        showErrorIcon,
    } = props;
    const [showPassword, setShowPassword] = useState(false);

    const isMobile = !useMediaQuery('(min-width:600px)');
    const language = getLanguageName();
    const endAdornment = (infoHelpText)
        ? (
            <InputAdornment position="end" className={classes.iconInfoContainer}>
                <IconButton
                    className={classes.iconInfoHelpText}
                    onClick={toggleDisplayFieldInfo}
                >
                    {infoHelpText && (showInfo ? <Cancel /> : <Info />)}
                </IconButton>
            </InputAdornment>
        ) : '';

    const plantsPasswordAdornment = () => {
        if (inputFieldType === 'password') {
            return showPassword ? <VisibilityOff onClick={() => setShowPassword(false)} style={{ cursor: 'pointer' }} /> : <Visibility onClick={() => setShowPassword(true)} style={{ cursor: 'pointer' }} />;
        }

        return '';
    };

    const plantsAdornment = () => {
        if (flagError || val) {
            return (
                <InputAdornment position="end">
                    {plantsPasswordAdornment()}
                    {flagError ? <Cancel color="error" /> : <CheckCircleIcon className={classes.successIcon} />}
                </InputAdornment>
            );
        }

        return plantsPasswordAdornment();
    };

    const getInputType = (inputType) => {
        if (inputType === 'password') {
            return showPassword ? 'text' : 'password';
        }

        return inputType;
    };

    const customInputJsx = () => (
        <Input
            classes={{
                root: classes.rootInputBase,
                input: classes.rootInput,
            }}
            disableUnderline
            id={fieldId}
            autoComplete={autoComplete}
            required={isRequired}
            name={name}
            type={getInputType(inputFieldType)}
            placeholder={placeholder}
            key={`tf_input_${fieldId}`}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleOnFocus}
            onKeyUp={handleOnKeyUp}
            onKeyPress={handleOnKeyPress}
            value={val}
            error={flagError}
            fullWidth
            data-test={`checkout-text-field-div-wrapper-${name}`}
            inputProps={
                {
                    ...InputProps,
                    'data-testid': `input-${fieldId}`, // T
                    'aria-label': name, // Added aria-label to provide an accessible

                }
            }
            disabled={disabled}
            endAdornment={isPlants && showErrorIcon ? plantsAdornment() : endAdornment}
            startAdornment={startAdornment ? <InputAdornment position="start">{startAdornment}</InputAdornment> : ''}
        />
    );

    const TooltipWrapperForPlants = () => (isPlants ? <Tooltip title={errorMessage} open={!!(errorMessage && errorMessage.length)} placement={isMobile ? 'bottom' : 'right'} arrow>{customInputJsx()}</Tooltip> : customInputJsx());

    return (
        <div>
            <FormControl
                classes={{
                    root: isBorderColor ? classes.withBorderColorRootFormControl : classes.withoutBorderColorRootFormControl,
                }}
                className={((showIntputBox) ? classes.noBorderTop : '')}
                error={flagError}
            >
                <InputLabel
                    className={inputFieldType === 'tel' && language === '/fr' ? classes.phoneLabel : null}
                    classes={{
                        root: classes.rootInputLabel,
                    }}
                    // shrink
                    htmlFor={name}
                >
                    <span>
                        <span>{label}</span>
                        {isRequired && <span className={classes.asteriskInputLabel}>*</span>}
                        {!flagError && isRequired && val && (
                            <svg
                                className={classes.checkmark}
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 52 52"
                            >
                                <circle className={classes.checkmarkCircle} cx="26" cy="26" r="25" fill="none" />
                                <path className={classes.checkmarkCheck} fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                            </svg>
                        )}
                    </span>
                </InputLabel>
                {TooltipWrapperForPlants()}
                {!isPlants && showInfo && (
                    <FormHelperText
                        id={`helper-text-${fieldId}`}
                        inputlabelprops={{
                            shrink: true,
                        }}
                        classes={{
                            root: `${classes.rootFormHelperText} ${classes.infoColor}`,
                        }}
                    >
                        <span className={classes.infoArrow} />
                        <span className={classes.formHelperText}>
                            {infoHelpText}
                        </span>
                    </FormHelperText>
                )}
                {!isPlants && flagError && (
                    <FormHelperText
                        id={`helper-text-error-${fieldId}`}
                        inputlabelprops={{
                            shrink: true,
                        }}
                        classes={{
                            root: `${classes.rootFormHelperText} ${classes.errorColor}`,
                        }}
                    >
                        <span className={classes.formHelperText}>
                            {errorMessage}
                        </span>
                    </FormHelperText>
                )}
            </FormControl>
        </div>
    );
};

CustomInputField.defaultProps = {
    label: '',
    fieldId: '',
    inputFieldType: '',
    classes: {},
    showInfo: false,
    isRequired: false,
    name: '',
    val: '',
    infoHelpText: '',
    errorMessage: null,
    flagError: null,
    InputProps: {},
    placeholder: '',
    disabled: false,
    autoComplete: 'off',
    handleOnFocus: () => { },
    handleOnKeyUp: () => { },
    handleChange: () => { },
    handleBlur: () => { },
    toggleDisplayFieldInfo: () => { },
    handleOnKeyPress: () => { },
    showIntputBox: false,
    startAdornment: null,
    isPlants: false,
    isBorderColor: true,
    showErrorIcon: true,
};

CustomInputField.propTypes = {
    label: string,
    fieldId: string,
    inputFieldType: string,
    classes: object,
    showInfo: bool,
    isRequired: bool,
    val: string,
    infoHelpText: string,
    name: string,
    handleOnFocus: func,
    handleOnKeyUp: func,
    handleOnKeyPress: func,
    errorMessage: string,
    flagError: bool,
    InputProps: object,
    placeholder: string,
    disabled: bool,
    autoComplete: string,
    handleChange: func,
    handleBlur: func,
    toggleDisplayFieldInfo: func,
    showIntputBox: bool,
    startAdornment: string,
    isPlants: bool,
    isBorderColor: bool,
    showErrorIcon: bool,
};

export default withStyles(styles)(CustomInputField);
